
import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, injectIntl } from 'react-intl'
import { injectStripe } from 'react-stripe-elements'
import { Button, Flex, Box, Copy, Icon, DotsLoader } from '@elparking/components'

import FirstNameField from 'commons/js/ui/FirstNameField'
import LastNameField from 'commons/js/ui/LastNameField'
import EmailField from 'commons/js/ui/EmailField'
import PhoneField from 'commons/js/ui/PhoneField'
import TermsField from 'commons/js/ui/TermsField'

import StripeErrorMessage from 'commons/js/ui/StripeErrorMessage'
import { LocalForm } from 'commons/js/components/form'

import * as api from '../../../api'
import { Container } from '../../../ui'
import Alert from '../../../ui/Alert'
import Success from '../../Success'
import ErrorMessage from '../../ErrorMessage'
import CreditCardForm from './CreditCardForm'
import usePaymentProcessor from './usePaymentProcessor'
import messages from './messages'
import Routes from 'commons/js/routes/routes'
import { TRADENAME } from 'commons/js/constants'

const TermsLink = () => (
    <a rel='noopener noreferrer' href={Routes.termsAndConditionsPath()} target='_blank'>
        <Copy as='span' fontColor='main'>
            <FormattedMessage defaultMessage='Términos y Condiciones de {tradename}' values={{
                tradename: TRADENAME,
            }} />
        </Copy>
    </a>
)

const CookiesLink = () => (
    <a rel='noopener noreferrer' href={Routes.cookiesPath()} target='_blank'>
        <Copy as='span' fontColor='main'>
            <FormattedMessage defaultMessage='Política de Privacidad' />
        </Copy>
    </a>
)

const PaymentForm = ({ town, fine, paymentGatewayId, onBack, onSuccess, brandId, payFine, createSetupIntent, intl, stripe }) => {
    const createSetupIntentCallback = useCallback(
        () => createSetupIntent({ paymentGatewayId }),
        [paymentGatewayId, createSetupIntent]
    )
    const handleCardSetupCallback = useCallback(
        (clientSecret) => stripe.handleCardSetup(clientSecret),
        [stripe]
    )
    const payFineCallback = useCallback(
        (data) => payFine({
            bulletinNumber: fine.bulletinNumber,
            townId: town.id,
            brandId,
            ...data,
        }),
        [fine, town, payFine, brandId]
    )

    const { payment, loading, error, creditCardError, processPayment } = usePaymentProcessor({
        createSetupIntentCallback,
        handleCardSetupCallback,
        payFineCallback,
    })
    const hasErrors = error || creditCardError

    if (payment) {
        return (
            <Success
              onBack={() => onSuccess()}
              message={<FormattedMessage defaultMessage='Pago realizado correctamente' />}
              buttonMessage={<FormattedMessage defaultMessage='Buscar otra denuncia' />}
            />)
    }

    return (
        <Container key='form' width='600px'>
            <LocalForm
              disabled={loading}
              onSubmit={(form) => { processPayment({...form}) }}
              model='payment'
            >
                <Flex flexWrap='wrap'>
                    <Box w='50%' pr='0.2em' mb='1em'>
                        <Button.unstyled type='button' onClick={onBack} disabled={fine.isCancelled} data-test='back-button'>
                            <Copy fontColor='main' transform='uppercase'>
                                <Icon type='left' /> <FormattedMessage defaultMessage='Ver detalle' />
                            </Copy>
                        </Button.unstyled>
                    </Box>
                    <Box pl='0.2em' w='50%' textAlign='right'>
                        <Copy fontWeight='book' fontSize='xxxSmall' color='gray60' transform='uppercase' padding='0'>
                            <FormattedMessage defaultMessage='Número de boletín' />
                        </Copy>
                        <Copy fontSize='medium' fontWeight='book' padding='0'>
                            {fine.bulletinNumber}
                        </Copy>
                    </Box>
                    {hasErrors && <Box w='100%' my='1em'>
                        <Alert type='error'>
                            <Copy data-test='error-message' fontWeight='book' padding='0'>
                                {error && <ErrorMessage error={error} />}
                                {creditCardError && <StripeErrorMessage stripeError={creditCardError} />}
                            </Copy>
                        </Alert>
                    </Box>}
                    <Box w='100%'>
                        <Box background='lightGray20' p='0.5em'>
                            <Copy transform='uppercase' padding='0 0 0.5em 0' fontSize='small' fontColor='gray60'>
                                <FormattedMessage defaultMessage='Datos personales' />
                            </Copy>
                            <Flex flexWrap='wrap'>
                                <Box w='100%'>
                                    <FirstNameField
                                      model='.firstName'
                                      disabled={loading}
                                      width='100%'
                                      label={intl.formatMessage(messages.firstName)}
                                      placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
                                      validateIcon
                                    />
                                </Box>
                                <Box w='100%' mt='1em'>
                                    <LastNameField
                                      model='.lastName'
                                      required
                                      disabled={loading}
                                      width='100%'
                                      label={intl.formatMessage(messages.lastName)}
                                      placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
                                      validateIcon
                                    />
                                </Box>
                                <Box w='100%' mt='1em'>
                                    <EmailField
                                      model='.email'
                                      disabled={loading}
                                      width='100%'
                                      placeholder={intl.formatMessage(messages.emailPlaceholder)}
                                      label={intl.formatMessage(messages.email)}
                                      validateIcon
                                    />
                                </Box>
                                <Box w='100%' mt='1em'>
                                    <PhoneField
                                      model='.phone'
                                      disabled={loading}
                                      width='100%'
                                      label={intl.formatMessage(messages.phone)}
                                      validateIcon
                                    />
                                </Box>
                            </Flex>
                        </Box>
                        <Box background='lightGray20' p='0.5em' mt='1em'>
                            <Copy transform='uppercase' padding='1em 0 0.5em 0' fontSize='small' fontColor='gray60'>
                                <FormattedMessage defaultMessage='Datos de pago' />
                            </Copy>
                            <Flex flexWrap='wrap'>
                                <CreditCardForm />
                            </Flex>
                        </Box>
                        <Box w='100%' mt='1em'>
                            <TermsField
                              model='.acceptConditions'
                              id='.acceptConditions'
                              fontSize='xSmall'
                              label={
                                  <Copy padding='0' fontWeight='regular' size='xxSmall'>
                                      <FormattedMessage
                                        defaultMessage='Al acceder y pagar una denuncia (i) estás aceptando crear una cuenta con {tradename}, -un tercero que permite a los clientes acceder y pagar denuncias-, y (ii) estás aceptando los {termsAndConditionsLink} y la {cookiesLink}'
                                        values={{
                                            tradename: TRADENAME,
                                            termsAndConditionsLink: <TermsLink />,
                                            cookiesLink: <CookiesLink />,
                                        }}
                                    />
                                  </Copy>
                                }
                            />
                        </Box>
                        <Box w='100%' mt='1em' textAlign='right'>
                            <Button type='submit' backgroundColor='accent' disabled={loading} data-test='pay-button' formNoValidate>
                                { loading
                                    ? <DotsLoader />
                                    : <><FormattedMessage defaultMessage='Realizar pago' /> <Icon type='right' color='white' /></>
                                }
                            </Button>
                        </Box>
                    </Box>
                </Flex>
            </LocalForm>
        </Container>
    )
}

PaymentForm.propTypes = {
    town: PropTypes.object.isRequired,
    fine: PropTypes.object.isRequired,
    paymentGatewayId: PropTypes.number,
    onBack: PropTypes.func,
    onSuccess: PropTypes.func,
    payFine: PropTypes.func,
    createSetupIntent: PropTypes.func,
    intl: PropTypes.object,
    stripe: PropTypes.object,
    brandId: PropTypes.number.isRequired,
}

PaymentForm.defaultProps = {
    onBack: () => {},
    payFine: api.payFine,
    createSetupIntent: api.createSetupIntent,
}

export default injectStripe(injectIntl(PaymentForm))
